import {
  ASSUMED_DELIVERED_EXTERNAL_STATUS,
  CommunicationStatus,
  getAttemptedPeppolStatus,
  getCurrentVersionSends,
  getInvoiceLastSentVersion,
  getInvoiceLifecycleErrorCode,
  getOwnDocumentPeriodStarts,
  getPeppolStatus,
  InvoiceDeliveryChannel,
  InvoiceLifecycleErrorCode,
  InvoicePeppolCustomValueName,
  InvoicePeppolStatus,
  InvoiceStatus,
  InvoiceType,
  isDraftSettled,
  isEarlyDraftInvoice,
  isEarlyDraftPendingRegeneration,
  isInvoiceDeliveredOnPeppol,
  isInvoicePeppolSendInProgress,
  isInvoiceSentBefore,
  isNilOrEmpty,
  isOffLedgerReceivableInvoice,
  isSyncWithExactOnline,
  POSTBIRD_DELIVERED_STATUSES,
  POSTBIRD_ERROR_STATUSES,
  planLvoCursorRollback,
  POSTBIRD_NOT_COLLECTED_STATUSES,
  readReversedBy,
  type Communication,
  type Invoice,
  type InvoiceWithPostings,
  type LeaseVariousOperation,
} from '@rentguru/commons-utils';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import keyBy from 'lodash/keyBy';

const GENERIC_ERROR_MESSAGE_ID = 'error.apicall';

const LEASE_LIFECYCLE_TYPES: InvoiceType[] = [
  InvoiceType.RENT,
  InvoiceType.LEASE_VARIOUS_OPERATION,
  InvoiceType.VARIOUS_OPERATION,
  InvoiceType.VARIOUS_EXCEPTIONAL_REVENUE,
];

const VALIDATED_STATUSES: InvoiceStatus[] = [InvoiceStatus.POSTED, InvoiceStatus.INVOICED];

const SES_DELIVERED_EXTERNAL_STATUS = 'DELIVERED';
const SES_REJECTED_EXTERNAL_STATUS = 'REJECTED';

const DELIVERED_CHANNEL_STATUSES: string[] = [
  CommunicationStatus.SENT,
  CommunicationStatus.SENT_MANUALLY,
  CommunicationStatus.DOWNLOADED,
  SES_DELIVERED_EXTERNAL_STATUS,
  ASSUMED_DELIVERED_EXTERNAL_STATUS,
  ...POSTBIRD_DELIVERED_STATUSES,
];

const FAILED_CHANNEL_STATUSES: string[] = [
  CommunicationStatus.ERROR,
  SES_REJECTED_EXTERNAL_STATUS,
  ...POSTBIRD_ERROR_STATUSES,
  ...POSTBIRD_NOT_COLLECTED_STATUSES,
  InvoicePeppolStatus.ERROR,
  InvoicePeppolStatus.FAILED,
];

const isPeppolDeliveredStatus = (status: string): boolean =>
  isInvoiceDeliveredOnPeppol([{ name: InvoicePeppolCustomValueName.PEPPOL_STATUS_FOR_CUSTOM_VALUE, value: status }]);

export enum ResetToDraftDialogVariant {
  NOT_SENT = 'NOT_SENT',
  SENT = 'SENT',
  PEPPOL_DELIVERED = 'PEPPOL_DELIVERED',
}

export enum InvoiceRecipient {
  OWNER = 'OWNER',
  TENANT = 'TENANT',
}

export enum SentChannelTone {
  DELIVERED = 'DELIVERED',
  FAILED = 'FAILED',
  OTHER = 'OTHER',
}

export interface InvoiceLifecycleActions {
  canValidate: boolean;
  canResetToDraft: boolean;
  canSend: boolean;
  canDelete: boolean;
  canShowVersions: boolean;
  canDuplicate: boolean;
  /** Why Validate is off on a draft the user could otherwise validate. */
  validateBlockedMessageId: string | null;
  /** Why editing the lines is off on a draft the user could otherwise edit. */
  editLinesBlockedMessageId: string | null;
}

const DOCUMENT_NOT_READY_RETRY_DELAYS_IN_MS = [500, 1000, 2000];

const waitFor = (delayInMs: number) => new Promise((resolve) => setTimeout(resolve, delayInMs));

export const fetchUntil = async <T>(
  fetch: () => Promise<T>,
  isComplete: (value: T) => boolean,
  delaysInMs: number[] = DOCUMENT_NOT_READY_RETRY_DELAYS_IN_MS
): Promise<T> => {
  const value = await fetch();
  const [delayInMs, ...nextDelaysInMs] = delaysInMs;
  if (isComplete(value) || isNil(delayInMs)) {
    return value;
  }
  await waitFor(delayInMs);
  return fetchUntil(fetch, isComplete, nextDelaysInMs);
};

export const retryWhileDocumentNotReady = async <T>(
  action: () => Promise<T>,
  delaysInMs: number[] = DOCUMENT_NOT_READY_RETRY_DELAYS_IN_MS
): Promise<T> => {
  try {
    return await action();
  } catch (error) {
    const [delayInMs, ...nextDelaysInMs] = delaysInMs;
    const isNotReady =
      getInvoiceLifecycleErrorCode((error as Error)?.message) === InvoiceLifecycleErrorCode.DOCUMENT_NOT_READY;
    if (!isNotReady || isNil(delayInMs)) {
      throw error;
    }
    await waitFor(delayInMs);
    return retryWhileDocumentNotReady(action, nextDelaysInMs);
  }
};

export const getInvoiceLifecycleErrorMessageId = (
  message: string | null | undefined,
  fallbackMessageId: string = GENERIC_ERROR_MESSAGE_ID
): string => {
  const code = getInvoiceLifecycleErrorCode(message);
  return isNil(code) ? fallbackMessageId : `invoiceLifecycle.errors.${code}`;
};

// A sent number stays the document's own: only a never-sent document may take the counter's next number
export const canValidateWithNextNumber = (
  invoice: Pick<Invoice, 'invoiceNumber' | 'sentVersions'>,
  errorMessage: string | null | undefined
): boolean =>
  !isNil(invoice.invoiceNumber) &&
  !isInvoiceSentBefore(invoice) &&
  getInvoiceLifecycleErrorCode(errorMessage) === InvoiceLifecycleErrorCode.NUMBER_TAKEN;

export const isLastSentVersionOnPeppol = (invoice: Pick<Invoice, 'sentVersions'>): boolean =>
  getInvoiceLastSentVersion(invoice)?.channels.some(({ channel }) => channel === InvoiceDeliveryChannel.PEPPOL) ??
  false;

const isCompanyInvoice = (invoice: Pick<Invoice, 'type'>): boolean => invoice.type === InvoiceType.COMPANY;

export const getInvoiceRecipient = (invoice: Pick<Invoice, 'type'>): InvoiceRecipient =>
  isCompanyInvoice(invoice) ? InvoiceRecipient.OWNER : InvoiceRecipient.TENANT;

export const getSentChannelTone = (status: string | null | undefined): SentChannelTone => {
  if (isNil(status)) {
    return SentChannelTone.OTHER;
  }
  if (DELIVERED_CHANNEL_STATUSES.includes(status) || isPeppolDeliveredStatus(status)) {
    return SentChannelTone.DELIVERED;
  }
  if (FAILED_CHANNEL_STATUSES.includes(status)) {
    return SentChannelTone.FAILED;
  }
  return SentChannelTone.OTHER;
};

/** Peppol counts once delivered: a failed or pending Peppol status alone leaves the version validated, not sent. */
export const isCurrentInvoiceVersionSent = (
  currentVersionSends: Pick<Communication, 'id'>[],
  customValues: Invoice['customValues']
): boolean => !isEmpty(currentVersionSends) || isInvoiceDeliveredOnPeppol(customValues);

export const hasSentDocumentCommunication = (
  invoice: Pick<Invoice, 'id' | 'sentVersions'>,
  communications: Pick<Communication, 'id' | 'invoiceId' | 'type' | 'channel' | 'status' | 'externalStatus'>[]
): boolean =>
  getCurrentVersionSends(invoice, communications).some((communication) => communication.invoiceId === invoice.id);

export const getResetToDraftDialogVariant = (
  invoice: Pick<Invoice, 'customValues' | 'sentVersions' | 'status' | 'type'>,
  hasSentCommunication: boolean
): ResetToDraftDialogVariant => {
  if (isInvoiceDeliveredOnPeppol(invoice.customValues)) {
    return ResetToDraftDialogVariant.PEPPOL_DELIVERED;
  }
  const isSentOwnerInvoice = isCompanyInvoice(invoice) && invoice.status === InvoiceStatus.INVOICED;
  if (
    hasSentCommunication ||
    !isNil(getAttemptedPeppolStatus(invoice.customValues)) ||
    !isNilOrEmpty(invoice.sentVersions) ||
    isSentOwnerInvoice
  ) {
    return ResetToDraftDialogVariant.SENT;
  }
  return ResetToDraftDialogVariant.NOT_SENT;
};

const hasLifecyclePolicy = (invoice: InvoiceWithPostings): boolean =>
  !isOffLedgerReceivableInvoice(invoice.postings ?? []) &&
  (isCompanyInvoice(invoice) || LEASE_LIFECYCLE_TYPES.includes(invoice.type));

const isSendable = (invoice: InvoiceWithPostings): boolean => {
  const peppolStatus = getPeppolStatus(invoice.customValues);
  const hasInvoiceNumber = !isNilOrEmpty(invoice.invoiceNumber);
  if (!isCompanyInvoice(invoice)) {
    return (
      invoice.status === InvoiceStatus.INVOICED &&
      hasInvoiceNumber &&
      !isNilOrEmpty(invoice.sentVersions) &&
      isNilOrEmpty(invoice.communicationId) &&
      isNil(peppolStatus)
    );
  }
  return (
    hasInvoiceNumber &&
    (invoice.status === InvoiceStatus.POSTED ||
      (invoice.status === InvoiceStatus.INVOICED && peppolStatus === InvoicePeppolStatus.ERROR))
  );
};

// A settled draft is not rebuilt: the finalizer issues it as drafted, with a correction document for the line
const getPendingRegenerationMessageIds = (
  invoice: InvoiceWithPostings
): Pick<InvoiceLifecycleActions, 'validateBlockedMessageId' | 'editLinesBlockedMessageId'> => {
  if (isDraftSettled(invoice, invoice.postings ?? [])) {
    return {
      validateBlockedMessageId: 'invoiceLifecycle.validate.settledDraftPendingCorrection',
      editLinesBlockedMessageId: 'lease.detail.invoice.detail.editLinesSettledPendingCorrection',
    };
  }
  return {
    validateBlockedMessageId: `invoiceLifecycle.errors.${InvoiceLifecycleErrorCode.EARLY_DRAFT_PENDING_REGENERATION}`,
    editLinesBlockedMessageId: 'lease.detail.invoice.detail.editLinesPendingRegeneration',
  };
};

export const getInvoiceLifecycleActions = (
  invoice: InvoiceWithPostings,
  canWrite: boolean,
  canDelete: boolean
): InvoiceLifecycleActions => {
  const hasPolicy = hasLifecyclePolicy(invoice);
  const canChange = canWrite && hasPolicy;
  const isDraft = invoice.status === InvoiceStatus.DRAFT;
  const hasLinkedPayment = (invoice.postings ?? []).some((posting) => !isNil(posting.transactionId));
  const isPeppolSendInProgress = isInvoicePeppolSendInProgress(invoice.customValues);
  const isLockedByExactOnline = !isCompanyInvoice(invoice) && isSyncWithExactOnline(invoice.customValues);
  const isClaimedByCreditNote = !isNil(readReversedBy(invoice.customValues));
  const isInStatement = (invoice.postings ?? []).some((posting) => !isNil(posting.statementId));
  const isPendingRegeneration = isEarlyDraftPendingRegeneration(invoice);
  return {
    canValidate: canChange && isDraft && !isPendingRegeneration,
    canResetToDraft:
      canChange &&
      VALIDATED_STATUSES.includes(invoice.status) &&
      !hasLinkedPayment &&
      !isInStatement &&
      !isPeppolSendInProgress &&
      !isLockedByExactOnline &&
      !isClaimedByCreditNote,
    canSend: canChange && isSendable(invoice) && !isPeppolSendInProgress,
    canDelete:
      canDelete && hasPolicy && isDraft && !hasLinkedPayment && !isInStatement && !isEarlyDraftInvoice(invoice),
    canShowVersions: isInvoiceSentBefore(invoice),
    canDuplicate: canChange && !isPendingRegeneration,
    ...(canChange && isPendingRegeneration
      ? getPendingRegenerationMessageIds(invoice)
      : { validateBlockedMessageId: null, editLinesBlockedMessageId: null }),
  };
};

export const canAddLeaseCreditNote = (
  invoice: Pick<Invoice, 'status' | 'creditNote' | 'type' | 'leaseId' | 'paid' | 'amount'>
): boolean =>
  invoice.status === InvoiceStatus.INVOICED &&
  !invoice.creditNote &&
  invoice.type !== InvoiceType.VARIOUS_OPERATION &&
  !isNil(invoice.leaseId) &&
  !invoice.paid &&
  invoice.amount !== 0;

export const shouldWarnPeriodNotRebilledOnDelete = (
  invoice: Pick<
    InvoiceWithPostings,
    'status' | 'type' | 'invoiceNumber' | 'sentVersions' | 'customValues' | 'postings'
  >,
  leaseVariousOperations: Pick<
    LeaseVariousOperation,
    | 'id'
    | 'type'
    | 'startDate'
    | 'endDate'
    | 'paymentFrequency'
    | 'paymentInvoicePeriod'
    | 'paymentInvoicePeriodCustomDay'
    | 'lastInvoiceDate'
  >[]
): boolean => {
  const isValidatedBefore =
    invoice.status === InvoiceStatus.DRAFT &&
    LEASE_LIFECYCLE_TYPES.includes(invoice.type) &&
    (!isNilOrEmpty(invoice.invoiceNumber) || !isNilOrEmpty(invoice.sentVersions));
  const periodStarts = getOwnDocumentPeriodStarts(invoice, invoice.postings);
  if (!isValidatedBefore || isEmpty(periodStarts)) {
    return isValidatedBefore;
  }
  const operationsById = keyBy(leaseVariousOperations, 'id');
  // A rolled-back cursor lets the nightly run issue the period again; a kept or unknown one does not (REN-8424)
  return Array.from(periodStarts).some(([operationId, periodStart]) => {
    const operation = operationsById[operationId];
    return isNil(operation) || planLvoCursorRollback(operation, periodStart).kind === 'KEEP';
  });
};
